// Keeping track of all the events that are being tracked in the mixpanel analytics
// TODO: Normalize the event names to be used in the analytics across the codebase
// TODO: Remove `(string & {})` once all events are listed here & moved to TypeScript
// TODO: Add not only event names but also the data that is being tracked for each event
type AnalyticsEvent = (string & {})
| "accept offer"
| "add payment info"
| "add_payment_details"
| "Autobook_Hours_Set"
| "autoLogin/landing - error"
| "decline offer"
| "DO_Autobook_Clicked"
| "instabook shift"
| "Instabook_Button_Clicked"
| "Instabook_Confirmed"
| "log in"
| "login - error"
| "logout"
| "mark shift as completed"
| "Password Reset - error"
| "pay invoice"
| "payment_required_modal_dismissed"
| "payment_required_modal_shown"
| "post shift"
| "postJob viewed"
| "offer details viewed"
| "revenue_card_add_failed"
| "revenue_card_add_succeeded"
| "resubmitted shift"
| "Select-Route"
| "send offer"
| "shift_detail_viewed"  // NEW: BI tracking for shift view-to-offer analysis
| "view shifts"          // Enhanced with filter context and aggregated metrics
| "sign-up - acknowledge market terms"
| "sign-up - complete dental office details form"
| "sign-up - complete dental office form"
| "sign-up - complete profile"
| "sign-up - complete user info form"
| "sign-up - Confirm user type"
| "sign-up - create account"
| "sign-up - error"
| "sign-up - Failed to Save Profile"
| "sign-up - Failed to Save Qualifications and Skills"
| "sign-up - Saved Qualifications and Skills"
| "sign-up - view start page"
| "sign-up - page viewed"
| "sign-up - page submitted"
| "sign-up - page error"
| "sign-up - duplicate account"
| "sign-up - switched role"
| "scent_policy_modal_shown"
| "software_rating_modal_shown"
| "submit rating"
| "Support Agent Logged In"
| "upload resume"
| "view offer detail"
| "view offers"
| "view_office_createAccount"
| "view_talent_createAccount"
| "Viewed Job Board"
| "Viewed My Stats"
| "shift_optimizer_allow_partial_offers_click_OFF"
| "shift_optimizer_boost_this_shift_click"
| "shift_optimizer_instabook_click"
| "shift_optimizer_downtime_payment_click"
| "shift_optimizer_fix_it_click"
| "Boost Restricted - 123D"
| "Boost Turned Off"
| "Job Board Token Purchase Restricted - 123D"
| "review_ask_shown"
| "review_ask_clicked"
| "review_ask_not_now"
| "review_ask_never"

export const Analytics = {
    /**
     * Track a Mixpanel event.
     *
     * Reads window.TempStars at call time (not module-load time) so it always
     * gets the fully-initialised legacy-app Analytics object.  If TempStars.Analytics
     * is not yet available it falls back to calling window.mixpanel.track directly,
     * ensuring events are never silently dropped.  Async rejection from the legacy
     * track path is caught and logged so failures are visible in the console.
     */
    track: (eventName: AnalyticsEvent, data: any) => {
        console.log(`[Analytics] track: ${eventName}`, data);
        const w = window as any;
        const directTrack = () => {
            try {
                if (typeof w.mixpanel?.track === 'function') {
                    Promise.resolve(w.mixpanel.track(eventName, { ...data, app_version: w.TempStars?.version || 'web' }))
                        .catch((error: any) => console.error('[Analytics] direct track failed', error));
                }
            } catch (error) { console.error('[Analytics] direct track failed', error); }
        };

        // Preferred path: route through the legacy TempStars.Analytics.track which
        // handles isAgent lookup and any super-property registration.
        if (w.TempStars && typeof w.TempStars.Analytics?.track === 'function') {
            try { Promise.resolve(w.TempStars.Analytics.track(eventName, data)).catch((err: any) => {
                console.error(`[Analytics] TempStars.Analytics.track error for "${eventName}":`, err);
                // Fallback: call mixpanel directly if the legacy path threw.
                directTrack();
            }); } catch (_) { directTrack(); }
            return;
        }

        // Fallback path: TempStars.Analytics not available yet — call mixpanel directly.
        if (w.mixpanel && typeof w.mixpanel.track === 'function') {
            console.log(`[Analytics] direct mixpanel.track("${eventName}")`);
            directTrack();
            return;
        }

        // Neither path available — log so it shows in the console during QA.
        console.warn(`[Analytics] track("${eventName}") skipped — no Mixpanel available`);
    },
}

export type OfficeAnalyticsContext = {
    location_id: string | null;
    country: 'CA' | 'US' | null;
    marketing_source: string | null;
    entry_path: string | null;
    utm_source: string | null;
};

const SAFE_MARKETING_SOURCES = new Set([
    'google', 'google_search', 'google_ads', 'facebook', 'instagram', 'linkedin',
    'friend', 'word_of_mouth', 'referral', 'dental_association', 'conference',
    'industry_conference', 'partner', 'cleardent', 'other',
]);

const SAFE_UTM_SOURCES = new Set([
    'google', 'google_ads', 'bing', 'microsoft', 'facebook', 'instagram', 'meta', 'fb',
    'linkedin', 'tiktok', 'youtube', 'email', 'newsletter', 'partner',
    'referral', 'cleardent', 'direct', 'organic', 'other',
]);

const normalizeAnalyticsSource = (
    value: unknown,
    allowedValues: Set<string>,
): string | null => {
    if (typeof value !== 'string' || !value.trim()) return null;
    const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
    return allowedValues.has(normalized) ? normalized : 'other';
};

export const getSafeMarketingSource = (value: unknown): string | null => (
    normalizeAnalyticsSource(value, SAFE_MARKETING_SOURCES)
);

export const getSafeUtmSource = (value: unknown): string | null => (
    normalizeAnalyticsSource(value, SAFE_UTM_SOURCES)
);

/**
 * Return the non-sensitive office dimensions used by activation reports.
 * Values come from the authenticated account already held by the app; no PII
 * or payment-provider data is copied into analytics payloads.
 */
export const getOfficeAnalyticsContext = (
    locationId?: number | string | null,
): OfficeAnalyticsContext => {
    const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
    const locations = Array.isArray(currentUser?.locations) ? currentUser.locations : [];
    const hasExplicitLocation = locationId !== undefined && locationId !== null;
    const location = locations.find((item: any) => Number(item?.id) === Number(locationId))
        || (!hasExplicitLocation ? locations[0] : null)
        || null;
    const rawCountry = location?.country ?? currentUser?.dentist?.country;
    const country = Number(rawCountry) === 0
        ? 'CA'
        : Number(rawCountry) === 1
            ? 'US'
            : null;
    const entryPath = location?.landingPage
        ? String(location.landingPage).split(/[?#]/, 1)[0] || null
        : null;

    return {
        location_id: location?.id != null ? String(location.id) : locationId != null ? String(locationId) : null,
        country,
        marketing_source: getSafeMarketingSource(location?.marketingSource),
        entry_path: entryPath,
        utm_source: getSafeUtmSource(location?.utmSource),
    };
};

export type BoostRestrictionLocation = {
    id?: number | string | null;
    locationId?: number | string | null;
    isCentralBooked?: number | string | null;
    practiceName?: string | null;
};

export const is123DentistLocation = (location?: BoostRestrictionLocation | null): boolean =>
    Number(location?.isCentralBooked) === 2;

const normalizeLocationId = (location?: BoostRestrictionLocation | null): number | string | null => {
    const rawLocationId = location?.id ?? location?.locationId ?? null;
    if (rawLocationId === null || rawLocationId === undefined || rawLocationId === '') {
        return null;
    }

    const numericLocationId = Number(rawLocationId);
    return Number.isFinite(numericLocationId) ? numericLocationId : rawLocationId;
};

export const trackBoostRestricted123D = (
    location?: BoostRestrictionLocation | null,
    sourcePage?: 'post_job' | 'review_shift_details' | 'job_posted' | 'find_a_temp'
) => {
    Analytics.track('Boost Restricted - 123D', {
        locationId: normalizeLocationId(location),
        practiceName: location?.practiceName || '',
        ...(sourcePage ? { source_page: sourcePage } : {}),
    });
};

// Utility helpers for consistent analytics metadata
export type ClientPlatform = 'ios' | 'android' | 'web'

/**
 * Derive platform and client version consistently across the app.
 */
export const getPlatformAndVersion = (): { platform: ClientPlatform, clientVersion: string } => {
    const w: any = window as any;
    const isCordova = !!w?.cordova;
    const rawPlatform = String(w?.device?.platform || w?.cordova?.platformId || w?.TempStars?.runtime || '').toLowerCase();
    const platform: ClientPlatform = isCordova
        ? (rawPlatform.includes('ios') ? 'ios' : (rawPlatform.includes('android') ? 'android' : 'web'))
        : 'web';
    const clientVersion: string = w?.TempStars?.version || 'web';
    return { platform, clientVersion };
}

/**
 * Generate a unique insert_id for analytics events.
 */
export const generateInsertId = (): string => {
    // @ts-ignore (older TS lib dom may not include randomUUID typing)
    if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
        // @ts-ignore
        return window.crypto.randomUUID();
    }
    const tpl = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    return tpl.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}
